import Layout from "@wrappers/layouts/main-layout/Layout";
import AgGridLayout from "@wrappers/layouts/AgGridLayout";
import { GridApi, IServerSideGetRowsParams, ITooltipParams, RowNode, ValueFormatterParams } from "ag-grid-community";
import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useClearGridData, useGetModulesData, usePermissionCheck } from "@hooks/AllCustomHooks";
import { historyLogsSelectionChanged, titleCase } from "@ts/utils";
import { createDatasource, sendRequest } from "@ts/retrieve-data";
import { updateTitle } from "@features/titleSlice";
import { updateActiveModule } from "@features/activeModuleSlice";
import moment from "moment";
import * as Sentry from "@sentry/react";
import { IChannel, Log, TActiveClientId } from "@ts/common-types";
import { RevertActionCellRenderer } from "@/components/cell-renderers/AllCellRenderers";

const History: React.FC = () => {
    interface StringValue {
        value: string;
    }
    // eslint-disable-next-line
    const [gridApi, setGridApi] = useState<GridApi>();
    const clearGridData = useClearGridData();
    const [loading, setLoading] = useState(true);
    const channel = useSelector((state: IChannel) => state.channel.value);
    const activeClientId = useSelector(
        (state: TActiveClientId) => state.activeClientId.value
    );

    const [serverSideDataSource] = useState({
        getRows: async (params: IServerSideGetRowsParams) => {
            const { startRow, endRow, sortModel, filterModel } = params.request;
            
            try {
                setLoading(true);
                const response = await populateLogs({startRow, endRow, sortModel, filterModel});
                if ((response as Array<Log>).length) {
                    params.success({ rowData: response as Array<Log>});
                } else {
                    params.success({ rowData: [], rowCount: 0});
                }
            } catch (err) {
                params.fail();
                console.error(err);
                Sentry.captureException(err);
            } finally {
                setLoading(false)
            }
        },
    })
    const dispatch = useDispatch();

    const { canRevertActionInHistory } = usePermissionCheck();

    const coldefs = [
        {
            field: "log_date",
            headerName: "Date",
            maxWidth: 320,
            wrapText: true,
            filter: "agDateColumnFilter",
            checkboxSelection: true,
            sort: "desc",
            valueFormatter: (params: ValueFormatterParams) =>
                moment(+params.value).format("YYYY-MM-DD"),
            headerCheckboxSelection: true,
            headerCheckboxSelectionFilteredOnly: true,
        },
        {
            field: "log_time",
            headerName: "Time",
            maxWidth: 220,
            minWidth: 100,
            wrapText: true,
            // filter: CustomTimeFilter,
            // filterParams: {
            //     value: timeFilterVal,
            //     setValue: (val: string) => {
            //         setTimeFilterVal(val)
            //     }
            // },
            valueFormatter: (params: ValueFormatterParams) =>
                moment(+params.value).format("HH:mm"),
        },
        {
            field: "revert_action",
            headerName: "Revert",
            maxWidth: 80,
            cellRenderer: RevertActionCellRenderer,
            filter: false,
            sortable: false,
        },
        {
            field: "module",
            headerName: "Module",
            maxWidth: 320,
            valueFormatter: ({ value }: StringValue) => titleCase(value),
            wrapText: true,
            filter: "agSetColumnFilter",
            filterParams: {
                showTooltips: true,
                valueFormatter: ({ value }: StringValue) =>
                    titleCase(value),
                values: [
                    "overview",
                    "portfolios",
                    "campaigns",
                    "adgroups",
                    "keywords",
                    "products",
                    "rules",
                    "keyword-analytics",
                    "product-analytics",
                    "negative-keywords",
                    "manual",
                    "auto",
                    "reviniti",
                    "budget",
                    "placement",
                    "revstack",
                    "common-reports",
                    "change-log",
                    "settings",
                    "integrations",
                ],
            },
        },
        {
            field: "change_type",
            headerName: "Type",
            valueFormatter: ({ value }: StringValue) => titleCase(value),
            maxWidth: 150,
            filter: "agSetColumnFilter",
            filterParams: {
                showTooltips: true,
                valueFormatter: ({ value }: StringValue) =>
                    titleCase(value),
                values: [
                    "NA",
                    "bid-change",
                    "addition",
                    "negative-keyword-addition",
                    "portfolio",
                    "rename",
                    "budget-change",
                    "status-change",
                    "placement-change",
                ],
            },
        },
        {
            field: "record_name",
            headerName: "Record",
            sortable: false,
            valueFormatter: ({ value }: StringValue) => titleCase(value),
            wrapText: true,
            maxWidth: 500,
            filter: "agTextColumnFilter",
        },
        {
            headerName: "Record Type",
            field: "record_type",
            valueFormatter: ({ value }: StringValue) => titleCase(value),
            maxWidth: 110,
            filter: "agSetColumnFilter",
            filterParams: {
                showTooltips: true,
                valueFormatter: ({ value }: StringValue) =>
                    titleCase(value),
                values: [
                    "negative_keyword",
                    "portfolio",
                    "keyword",
                    "search_term",
                    "adgroup",
                    "video_keyword",
                    "campaign",
                    "NA",
                    "target",
                    "productad",
                ],
            },
        },
        {
            headerName: "Property",
            field: "attribute",
            valueFormatter: ({ value }: StringValue) => titleCase(value),
            maxWidth: 110,
            filter: "agSetColumnFilter",
            filterParams: {
                showTooltips: true,
                valueFormatter: ({ value }: StringValue) =>
                    titleCase(value),
                values: [
                    "campaign_name",
                    "state",
                    "budget",
                    "placementTop",
                    "placementProductPage",
                    "portfolioId",
                    "ad_group_name",
                    "create_campaign",
                    "create_target",
                    "create_negative_target",
                    "portfolio_name",
                    "bid",
                    "endDate"
                ],
            },
        },
        {
            field: "old_value",
            headerName: "From",
            tooltipValueGetter: ({ value }: ITooltipParams) => value,
            cellClass: "text-center",
            filter: "agTextColumnFilter",
            valueFormatter: (params: ValueFormatterParams) =>
                params.data.attribute === "endDate"
                    ? moment(+params.value).format("YYYY-MM-DD")
                    : params.value,
        },
        {
            field: "new_value",
            headerName: "To",
            tooltipValueGetter: ({ value }: ITooltipParams) => value,
            cellClass: "text-center",
            filter: "agTextColumnFilter",
            valueFormatter: (params: ValueFormatterParams) =>
                params.data.attribute === "endDate"
                    ? moment(+params.value).format("YYYY-MM-DD")
                    : params.value,
        },
        {
            field: "source",
            headerName: "Source",
            tooltipValueGetter: ({ value }: ITooltipParams) =>
                titleCase(value),
            valueFormatter: ({ value }: StringValue) => titleCase(value),
            filter: "agSetColumnFilter",
            filterParams: {
                showTooltips: true,
                valueFormatter: ({ value }: StringValue) =>
                    titleCase(value),
                values: ["reviniti"],
            },
        },
        {
            field: "nature",
            headerName: "Nature",
            tooltipValueGetter: ({ value }: ITooltipParams) =>
                titleCase(value),
            valueFormatter: ({ value }: StringValue) => titleCase(value),
            filter: "agSetColumnFilter",
            maxWidth: 220,
            minWidth: 100,
            filterParams: {
                showTooltips: true,
                valueFormatter: ({ value }: StringValue) =>
                    titleCase(value),
                values: ["auto", "manual"],
            },
        },
        {
            field: "source_name",
            headerName: "Source Name",
            tooltipValueGetter: ({ value }: ITooltipParams) =>
                titleCase(value),
            valueFormatter: ({ value }: StringValue) => titleCase(value),
            filter: "agSetColumnFilter",
            filterParams: {
                showTooltips: true,
                valueFormatter: ({ value }: StringValue) =>
                    titleCase(value),
                values: (params: any) => {
                    getSourceNameFilterVals()
                        .then((response) => {
                            params.success(
                                response.rows.map(
                                    (source: Record<string, string>) =>
                                        source.source_name
                                )
                            );
                        })
                        .catch((error) => {
                            params.success([]);
                            console.error(
                                "Error fetching filter values:",
                                error
                            );
                            ``;
                        });
                },
            },
        },
        {
            field: "source_id",
            headerName: "Source ID",
            hide: true,
            filter: false
        },
    ]
    const filteredColdefs = !canRevertActionInHistory ? coldefs.filter((cols) => cols.field !== "revert_action") : coldefs
    !canRevertActionInHistory ? filteredColdefs[0].checkboxSelection = false : filteredColdefs[0].checkboxSelection = true


    const columnDefs = useGetModulesData({
        type: "columns",
        colDefs: filteredColdefs, revertPermission: canRevertActionInHistory
    });

    const populateLogs = async (params : Record<any, any>) => {
        return await createDatasource("history", {
            dateObj: {},
            offset: params.startRow,
            pageSize: params.endRow - params.startRow,
            sortModel: params.sortModel,
            filterModel: params.filterModel
        });
    };
    
    const getSourceNameFilterVals = async () => {
        return await sendRequest({
            type: `history/sourcename-filter-val`,
            method: "get",
        })
    };

    useEffect(() => {
        clearGridData();
        dispatch(updateTitle("History"));
        dispatch(updateActiveModule("history"));
    }, [activeClientId, dispatch, channel]);

    return (
        <>
            <Layout>
                <div className="main-content-body">
                    <div
                        id="history-grid"
                        className="ag-theme-alpine"
                        style={{
                            height: "86%",
                            width: "100%",
                            maxHeight: "calc(100% - 92px)",
                        }}
                    >
                        <AgGridLayout
                            onSelectionChanged={historyLogsSelectionChanged}
                            columnDefs={columnDefs}
                            setGridApi={setGridApi}
                            isTotalRowsReq={false}
                            loading={loading}
                            rowHeight={45}
                            isRowSelectable={(rowNode: RowNode) =>
                                !(rowNode.data.change_type.includes("addition"))}
                            pagination={true}
                            rowModelType="serverSide"
                            serverSideDatasource={serverSideDataSource}
                            serverSideStoreType="partial"
                            loadingCellRenderer={() => null}
                        />
                    </div>
                </div>
            </Layout>
        </>
    );
};

export default History;
